// The pieces every Settings section is built from.
//
// Eight sections written by one session still produce eight slightly different
// blocks unless the block is a component. These are deliberately plain: §9 says
// Settings inherits the design system and needs no separate visual exercise, so
// there is no new visual language here — only the smallest set of parts that
// keeps eight sections looking like one surface.
import React from "react";

import { SectionTitle } from "../../components/PageHead";

/** A titled block within a section. The unit every setting lives in. */
export function Block({ title, note, actions, children }) {
  return (
    <section className="set-block">
      <div className="set-block-head">
        <div>
          <SectionTitle>{title}</SectionTitle>
          {note ? <p className="set-block-note dim">{note}</p> : null}
        </div>
        {actions ? <div className="set-block-actions">{actions}</div> : null}
      </div>
      <div className="set-block-body">{children}</div>
    </section>
  );
}

/** A label/value line. `hint` explains the setting; it never states a value. */
export function Row({ label, hint, control, children }) {
  return (
    <div className="set-row">
      <div className="set-row-label">
        <span>{label}</span>
        {hint ? <span className="set-row-hint dim">{hint}</span> : null}
      </div>
      <div className="set-row-control">{control || children}</div>
    </div>
  );
}

/** Read-only value. Used wherever the operator can see but not set something. */
export function Value({ children, mono }) {
  return (
    <span className={mono ? "set-value mono" : "set-value"}>
      {children === null || children === undefined || children === "" ? (
        <span className="dim">—</span>
      ) : (
        children
      )}
    </span>
  );
}

/**
 * A status dot. `tone` is the backend's own word where there is one —
 * `/integrations/health` answers green / yellow / red — so nothing is
 * re-interpreted on the way to the screen.
 */
export function StatusDot({ tone, label }) {
  const t = ["green", "yellow", "red"].includes(tone) ? tone : "unknown";
  return (
    <span className={`set-dot set-dot-${t}`}>
      <span className="set-dot-mark" aria-hidden="true" />
      <span>{label || t}</span>
    </span>
  );
}

export function Toggle({ id, checked, onChange, disabled, label }) {
  return (
    <label className="set-toggle" htmlFor={id}>
      <input
        id={id}
        type="checkbox"
        checked={!!checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="set-toggle-text">{label}</span>
    </label>
  );
}

export function Button({ onClick, children, disabled, kind }) {
  return (
    <button
      type="button"
      className={kind === "quiet" ? "btn-ghost btn-sm" : "btn-ghost btn-sm set-btn"}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </button>
  );
}

/** A wide table that scrolls inside itself rather than widening the page. */
export function Table({ head, children, empty }) {
  return (
    <div className="set-table-wrap">
      <table className="set-table">
        <thead>
          <tr>
            {head.map((h) => (
              <th key={h} scope="col">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {React.Children.count(children) ? (
            children
          ) : (
            <tr>
              <td className="dim" colSpan={head.length}>
                {empty || "Nothing to show."}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

/**
 * What to render for a resource that is not `ok`.
 *
 * The four non-ok states are given four different renderings on purpose. A
 * founder-gated endpoint refusing a non-founder is not a fault and must not
 * look like one (R5); a dark-launched capability is not a fault either; an
 * endpoint that answered with nothing gets the designed empty state (R6); only
 * a genuine failure gets the error treatment.
 */
export function Gate({ resource, empty, children }) {
  const { status } = resource;

  if (status === "loading") {
    return (
      <div className="set-loading" role="status" aria-live="polite">
        <span className="skeleton set-skel" />
        <span className="skeleton set-skel" />
        <span className="sr-only">Loading</span>
      </div>
    );
  }

  if (status === "denied") {
    return (
      <div className="state set-state" role="status">
        <strong>Founder only.</strong>{" "}
        <span className="dim">
          The server refused this read. That is the gate working, not a fault.
        </span>
      </div>
    );
  }

  if (status === "disabled") {
    return (
      <div className="state set-state" role="status">
        <strong>Not enabled.</strong>{" "}
        <span className="dim">
          This capability is not switched on in this environment.
        </span>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="state set-state set-state-error" role="alert">
        <strong>Could not load.</strong>{" "}
        <span className="dim">{resource.error || "The request failed."}</span>
      </div>
    );
  }

  if (status === "empty") {
    return (
      <div className="state set-state" role="status">
        {empty || "Nothing here yet."}
      </div>
    );
  }

  if (status === "skipped") return null;

  return children(resource.data);
}

/**
 * A section §9 names for which no endpoint exists.
 *
 * R6 says render the designed empty state rather than fabricate; §4 says log
 * it. This component is what "logged in the interface too" looks like — the
 * operator is told the section is specified and unbuilt, rather than shown an
 * empty panel they will assume is broken.
 */
export function NotBuilt({ what, why }) {
  return (
    <div className="state set-state set-notbuilt" role="status">
      <div className="set-notbuilt-title">{what}</div>
      <p className="dim">{why}</p>
    </div>
  );
}
